import React, { useEffect, useState } from "react";
import styles from "./RFZOOrderTherapy.module.scss";
import { useDispatch, useSelector } from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import sr from "date-fns/locale/sr-Latn";
import Calendar from "react-calendar";
import {
  RFZOArticleForMessage,
  rfzoTherapyActions,
} from "../../store/rfzoTherapy-slice";
import { RootState } from "../../store";
import { sendAnswerForRFZORequest } from "../../store/rfzoTherapy-actions";
import Button from "../UI/Buttons/Button/Button";
import moment from "moment";
import { validateTherapySchedule } from "../../utils/rfzo/therapyScheduleValidation";
registerLocale("sr", sr);

type Props = {
  rfzoId: number;
  show: boolean;
  backdropClickHandler: () => void;
  hasTherapy?: boolean;
};

export type RFZOMessage = {
  items: RFZOArticleForMessage[];
  willBeReady?: string;
  noteForUser?: string;
  lastTherapyDate?: any;
};

export const SEND_ANSWER_FAILED_MESSAGE =
  "Slanje odgovora nije uspelo. Proverite unos i pokušajte ponovo.";

export const ARTICLES_REQUIRED_MESSAGE =
  "Dodajte bar jedan artikal terapije pre slanja odgovora.";

const MONTH_NAMES_SR = [
  "januar",
  "februar",
  "mart",
  "april",
  "maj",
  "jun",
  "jul",
  "avgust",
  "septembar",
  "oktobar",
  "novembar",
  "decembar",
];

const formatDay = (date: Date) => moment(date).format("DD.MM.YYYY.");
const formatMonth = (date: Date) =>
  `${MONTH_NAMES_SR[date.getMonth()]} ${date.getFullYear()}.`;

const RFZOOrderTherapy = ({ hasTherapy, rfzoId }: Props) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [lastTherapyDate, setLastTherapyDate] = useState<Date | null>(null);
  const [willBeReady, setWillBeReady] = useState<string | null>(null);
  const [textMessage, setTextMessage] = useState<string>("");
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [articlesError, setArticlesError] = useState(false);
  // The failed flag in the store is global; only surface it for a send this
  // form actually issued, not one left over from a previous request.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const articlesForRFZO = useSelector(
    (state: RootState) => state.rfzoTherapy.articlesForRFZO,
  );
  const articlesForRFZOMessage = useSelector(
    (state: RootState) => state.rfzoTherapy.articlesForRFZOMessage,
  );
  const sendInProgress = useSelector(
    (state: RootState) => state.rfzoTherapy.sendAnswerForRFZORequestStart,
  );
  const sendFailed = useSelector(
    (state: RootState) => state.rfzoTherapy.sendAnswerForRFZORequestFailed,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(rfzoTherapyActions.trimRFZOArticlesForMessage());
  }, [articlesForRFZO]);

  useEffect(() => {
    if (startDate) {
      setWillBeReady(
        startDate.getFullYear().toString() +
          "-" +
          (("0" + (startDate.getMonth() + 1).toString()).slice(-2) +
            "-" +
            ("0" + startDate.getDate().toString()).slice(-2)),
      );
    } else {
      setWillBeReady(null);
    }
  }, [startDate]);

  // Re-validate as the pharmacist corrects the dates so a shown error clears
  // as soon as the pair becomes valid.
  useEffect(() => {
    setScheduleError((current) =>
      current === null
        ? null
        : validateTherapySchedule(startDate, lastTherapyDate),
    );
  }, [startDate, lastTherapyDate]);

  useEffect(() => {
    if (articlesForRFZOMessage.length > 0) {
      setArticlesError(false);
    }
  }, [articlesForRFZOMessage]);

  const send = () => {
    // "Korisnik ima terapiju" with no articles would reach the backend as an
    // empty answer, which it treats as "no therapy" and tells the patient so.
    const missingArticles = !!hasTherapy && articlesForRFZOMessage.length === 0;
    const error = validateTherapySchedule(startDate, lastTherapyDate);
    setArticlesError(missingArticles);
    setScheduleError(error);
    if (missingArticles || error) {
      return;
    }

    let rfzoMessage: RFZOMessage = {
      items: articlesForRFZOMessage,
    };
    if (willBeReady) {
      rfzoMessage = { ...rfzoMessage, willBeReady: willBeReady };
    }
    if (textMessage !== "") {
      rfzoMessage = { ...rfzoMessage, noteForUser: textMessage };
    }
    if (lastTherapyDate) {
      rfzoMessage = {
        ...rfzoMessage,
        lastTherapyDate: moment(lastTherapyDate).format("YYYY-MM-DD"),
      };
    }
    setHasSubmitted(true);
    dispatch<any>(sendAnswerForRFZORequest(rfzoId, rfzoMessage));
  };

  const today = new Date();

  return (
    <div>
      <div className={styles.DatePickerWrapper}>
        <div className={styles.header}>
          {hasTherapy && "Naredna terapija"}
          {!hasTherapy && "Datum dospeća terapije"}
        </div>
        <span>
          {hasTherapy && "Izaberi datum dospeća naredne terapije"}
          {!hasTherapy && "Izaberi datum"}
        </span>
        <div>
          <Calendar
            onChange={(val) => {
              // ts checking if array is sent -_-
              if (Array.isArray(val)) {
                setStartDate(new Date());
              } else {
                setStartDate(val);
              }
            }}
            value={startDate}
            locale="sr"
            className={styles.calendarWrap}
          />
        </div>
        <div
          className={styles.SelectionRow}
          data-testid="will-be-ready-selection"
        >
          {startDate ? (
            <>
              <span className={styles.SelectionValue}>
                Izabran datum: {formatDay(startDate)}
              </span>
              <button
                type="button"
                className={styles.ClearButton}
                onClick={() => setStartDate(null)}
              >
                Poništi izbor datuma
              </button>
            </>
          ) : (
            <span className={styles.SelectionEmpty}>Datum nije izabran</span>
          )}
        </div>
        {hasTherapy && (
          <span style={{ fontSize: "12px", color: "#00B2B1" }}>
            *Ukoliko je ovo poslednja terapija, nemojte izabrati datum
          </span>
        )}{" "}
        <br />
        <span>Izaberi poslednji mesec dospeća terapije</span>
        <Calendar
          onClickMonth={(val) => {
            // ts checking if array is sent -_-
            if (Array.isArray(val)) {
              setLastTherapyDate(new Date());
            } else {
              const date = new Date(val);
              date.setHours(date.getHours() + 12);
              setLastTherapyDate(date);
            }
          }}
          view="year"
          value={lastTherapyDate}
          minDate={startDate ?? today}
          locale="sr"
          defaultView="year"
          className={styles.calendarWrap}
        />
        <div
          className={styles.SelectionRow}
          data-testid="last-therapy-selection"
        >
          {lastTherapyDate ? (
            <>
              <span className={styles.SelectionValue}>
                Izabran mesec: {formatMonth(lastTherapyDate)}
              </span>
              <button
                type="button"
                className={styles.ClearButton}
                onClick={() => setLastTherapyDate(null)}
              >
                Poništi izbor meseca
              </button>
            </>
          ) : (
            <span className={styles.SelectionEmpty}>Mesec nije izabran</span>
          )}
        </div>
        {hasTherapy && (
          <span style={{ fontSize: "12px", color: "#00B2B1" }}>
            *Ovo polje farmaceut mora popuniti kada je izabran datum dospeća
          </span>
        )}
        {scheduleError && (
          <span className={styles.FieldError} role="alert">
            {scheduleError}
          </span>
        )}
        {hasTherapy && (
          <div id="NoteForUser" className={styles.NoteForUser}>
            <div style={{ width: "90%" }}>
              <textarea
                placeholder="Poruka za korisnika"
                className={styles.TextArea}
                onChange={(e) => setTextMessage(e.target.value)}
              ></textarea>
            </div>
          </div>
        )}
      </div>

      {articlesError && (
        <div className={styles.FieldError} role="alert">
          {ARTICLES_REQUIRED_MESSAGE}
        </div>
      )}

      {hasSubmitted && sendFailed && (
        <div className={styles.FieldError} role="alert">
          {SEND_ANSWER_FAILED_MESSAGE}
        </div>
      )}

      <div className={styles.ModalActions}>
        <Button buttonType="blue" onClick={send} disabled={sendInProgress}>
          Pošalji odgovor
        </Button>
      </div>
    </div>
  );
};

export default RFZOOrderTherapy;
